import {FC, HTMLAttributes, ReactNode} from 'react'
import {
    StyledDetailsFieldsGrid,
    StyledDetailsFieldsSection,
    StyledDetailsFieldsSectionHeading,
    StyledDetailsFieldsSectionBody,
    StyledDetailsPageWrapper,
    StyledDetailsPageContent,
    StyledDetailsSidebar,
    StyledDetailsSidebarTabs,
    StyledDetailsSidebarContent,
    StyledDetailsPageContentTabWrapper,
    StyledDetailsFieldsCardWrapper,
    StyledPlannedAccessEmptyState
} from '@/components/ui/details-atoms/style'
import {Flexbox, FlexboxProps} from '@components/ui/flexbox/FlexBox.tsx'
import {CSSProperties} from 'styled-components'
import {typography} from '@/theme/typography'
import {EmptyStateProps} from '@/components/commons/empty-state/EmptyState'
import {Text} from '@/components/ui/text/Text'

export const DetailsPageWrapper = ({isSuccess, ...props}: HTMLAttributes<HTMLDivElement> & {isSuccess: boolean}) => (
    <StyledDetailsPageWrapper {...props} $isSuccess={isSuccess} />
)
export const DetailsPageContent = (props: HTMLAttributes<HTMLDivElement>) => <StyledDetailsPageContent {...props} />
export const DetailsPageContentTabWrapper = (props: HTMLAttributes<HTMLDivElement>) => (
    <StyledDetailsPageContentTabWrapper {...props} />
)
export const DetailsSidebar = (props: HTMLAttributes<HTMLDivElement>) => <StyledDetailsSidebar {...props} />
export const DetailsSidebarTabs = (props: HTMLAttributes<HTMLDivElement>) => <StyledDetailsSidebarTabs {...props} />
export const DetailsSidebarContent = (props: HTMLAttributes<HTMLDivElement>) => (
    <StyledDetailsSidebarContent {...props} />
)

export interface DetailsFieldsSectionProps {
    variant?: 'primary' | 'neutral' | 'ghost'
    title: string
    className?: string
    subtitle?: string
    infoTitleSlot?: ReactNode
    headingEndSlot?: ReactNode
    isDisabled?: boolean
    bodyWidth?: CSSProperties['width']
    children: ReactNode
    width?: CSSProperties['width']
    height?: CSSProperties['height']
}
export const DetailsFieldsSection: FC<DetailsFieldsSectionProps> = ({
    title,
    subtitle,
    infoTitleSlot,
    className,
    headingEndSlot,
    isDisabled,
    bodyWidth,
    children,
    variant = 'ghost',
    width,
    height
}) => {
    return (
        <StyledDetailsFieldsSection
            className={className}
            $isDisabled={!!isDisabled}
            $variant={variant}
            $width={width}
            $height={height}
        >
            <StyledDetailsFieldsSectionHeading
                $variant={variant}
                className="section-header"
                justify="space-between"
                align="center"
                fullWidth
            >
                <Flexbox gap={4} align="center">
                    <Flexbox direction={'column'}>
                        <Text size="textLg" weight="500">
                            {title}
                        </Text>
                        {subtitle && (
                            <Text size="textSm" palette="neutral" shade="600">
                                {subtitle}
                            </Text>
                        )}
                    </Flexbox>
                    {infoTitleSlot && infoTitleSlot}
                </Flexbox>
                {headingEndSlot}
            </StyledDetailsFieldsSectionHeading>
            <StyledDetailsFieldsSectionBody className="section-body" $width={bodyWidth}>
                {children}
            </StyledDetailsFieldsSectionBody>
        </StyledDetailsFieldsSection>
    )
}

export const DetailsFieldsGrid = (props: HTMLAttributes<HTMLDivElement>) => <StyledDetailsFieldsGrid {...props} />

export const DetailsField = ({children, ...rest}: FlexboxProps<'div'>) => (
    <Flexbox direction="column" gap={1} {...rest}>
        {children}
    </Flexbox>
)

export interface DetailsInformationTextProps extends HTMLAttributes<HTMLParagraphElement> {
    variant?: 'primary' | 'secondary' | 'success' | 'warning'
    size?: keyof typeof typography
    isBold?: boolean
}

export const DetailsFieldsCard = ({children, size = 'md', ...rest}: FlexboxProps<'div'> & {size?: 'sm' | 'md'}) => (
    <StyledDetailsFieldsCardWrapper {...rest} $size={size}>
        {children}
    </StyledDetailsFieldsCardWrapper>
)

export const DetailsPlannedAccessEmptyState = (props: EmptyStateProps) => <StyledPlannedAccessEmptyState {...props} />
